"use client";

import { useRef, useState, type AnchorHTMLAttributes, type MouseEvent } from "react";
import type {} from "@/lib/analytics/types";

/** React owns checkout clicks, including links mounted after Lemon.js initializes. */
export default function CheckoutLink({
  href,
  onClick,
  children,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const opening = useRef(false);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleClick = async (event: MouseEvent<HTMLAnchorElement>) => {
    // Keep explicit new-tab/window gestures working like ordinary links.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    if (opening.current) return;
    opening.current = true;
    setPending(true);
    setFailed(false);

    try {
      // A click can arrive while the afterInteractive script is still downloading.
      const deadline = Date.now() + 10_000;
      while (!window.LemonSqueezy?.Url?.Open) {
        if (Date.now() >= deadline) throw new Error("Checkout script did not become ready");
        await new Promise((resolve) => window.setTimeout(resolve, 100));
      }
      window.LemonSqueezy.Url.Open(href);
      // Analytics failures must never prevent checkout from opening.
      try {
        onClick?.(event);
      } catch (error) {
        console.warn("Unable to track checkout:", error);
      }
    } catch (error) {
      console.warn("Unable to open checkout overlay:", error);
      setFailed(true);
    } finally {
      opening.current = false;
      setPending(false);
    }
  };

  // Unlaunched info products have no checkout URL yet.
  if (!href || href === "#") {
    return (
      <span className={props.className} role="link" aria-disabled="true">
        Coming soon
      </span>
    );
  }

  return (
    <>
      <a {...props} href={href} onClick={handleClick} aria-busy={pending}>
        {pending ? "Opening checkout…" : children}
      </a>
      {failed && (
        <span role="alert" className="block text-sm mt-2">
          Checkout couldn’t open. Try again or{" "}
          <a href={href} target="_blank" rel="noopener noreferrer" className="underline">
            open checkout in a new tab
          </a>
          .
        </span>
      )}
    </>
  );
}
